import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { isAfter } from 'date-fns';
import { faArrowDownToBracket } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { CoverImage } from '@components/basics/SongCard/Cover';
import { Button } from '@components/main-components/button/Button';
import {
  ASPECT_RATIOS,
  ScreenshotContainer,
} from '@components/main-components/download-screenshot/ScreenshotContainer';
import { CircleLogo } from '@components/main-components/media/logo/Logo';
import {
  getFragment,
  ReleaseShareImageInfoDocument,
  ReleaseShareImageInfoFragmentDoc,
  useQuery,
} from '@graphql/reactQuery';
import { useStableCallback } from '@hooks/useStableCallback';
import { downloadScreenshot } from '@utils/screenshot';

/*#__PURE__*/ gql(/* GraphQL */ `
  query releaseShareImageInfo($id: UUID!) {
    release(id: $id) {
      id
      ...ReleaseShareImageInfo
    }
  }

  fragment ReleaseShareImageInfo on Release {
    id
    webappUri
    title
    artist {
      id
      name
    }
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    publicMintStart
  }
`);

export const ShareReleaseImage = ({
  releaseId,
  onError,
  className,
  isLoading,
  fullWidthButton,
}: {
  releaseId: string;
  onError?: (err: unknown) => void;
  className?: string;
  isLoading?: boolean;
  fullWidthButton?: boolean;
}) => {
  const screenshotRef = useRef<HTMLDivElement>(null);

  const { data, isLoading: isReleaseLoading } = useQuery(ReleaseShareImageInfoDocument, {
    variables: {
      id: releaseId,
    },
    staleTime: 0,
  });

  const showSkeleton = isLoading || isReleaseLoading;

  const release = data?.data.release;

  const releaseInfo = getFragment(ReleaseShareImageInfoFragmentDoc, release);

  const publicMintStartDate = releaseInfo?.publicMintStart
    ? new Date(releaseInfo.publicMintStart)
    : null;

  const startDate =
    publicMintStartDate && isAfter(publicMintStartDate, new Date())
      ? new Intl.DateTimeFormat(
          'en-US',
          // 'MMMM d, h:mma z'
          {
            timeZone: 'America/New_York',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
          },
        ).format(publicMintStartDate) + ' EST'
      : null;

  const coverUrl = releaseInfo?.coverImage.url;
  const staticCoverUrl = releaseInfo?.staticCoverImage?.url;
  const coverImage = staticCoverUrl ?? coverUrl;

  const onDownload = useStableCallback(async () => {
    try {
      await downloadScreenshot({
        filename: `${releaseInfo?.artist.name}-${releaseInfo?.title}`,
        screenshotRef,
        options: {
          cacheBust: true,
          quality: 1,
          pixelRatio: 1,
          includeQueryParams: true,
        },
      });
    } catch (err) {
      onError?.(err);
    }
  });

  const skeletonBody = (
    <div className="relative animate-pulse">
      <div className="h-[393px] rounded-[10px] bg-base400 md:h-[372px]" />
      <div className="absolute bottom-0 flex w-full flex-col font-title text-white">
        <div className="pointer-events-none z-above2 h-20 w-full transform-gpu bg-gradient-to-t from-black md:h-[146px]" />
        <div className="flex w-full flex-row items-end justify-between rounded-b-[10px] bg-black p-5">
          <div className="flex flex-col gap-2">
            <div className="text-title-s font-medium leading-6 text-base400">
              <div className="h-6 w-20 bg-base400" />
            </div>
            <div className="text-[35px] font-medium leading-[100%]">
              <div className="h-[35px] w-40 bg-base400" />
            </div>
          </div>
          <CircleLogo className="h-[25px] w-[25px] flex-shrink-0" />
        </div>
      </div>
    </div>
  );

  const body = (
    <ScreenshotContainer
      className="relative rounded-[10px]"
      screenshotRef={screenshotRef}
      selectedAspectRatio={ASPECT_RATIOS[2]}
    >
      {coverImage && <CoverImage.Single src={coverImage} />}
      {startDate && (
        <div
          className={clsx(
            'absolute right-[10px] top-5 flex items-center backdrop-blur-lg',
            'mr-2 overflow-hidden rounded-full bg-black/30 px-4 py-2 text-[30px] leading-[100%]',
            'font-semibold text-white',
          )}
        >
          Starts {startDate}
        </div>
      )}
      <div className="absolute bottom-0 flex w-full flex-col font-title text-white">
        <div className="pointer-events-none z-above2 -mb-[1px] h-20 w-full transform-gpu bg-gradient-to-t from-black md:h-[146px]" />
        <div className="flex w-full flex-row items-end justify-between gap-2 bg-black p-10">
          <div className="flex flex-col gap-2">
            <div className="text-title-xxl font-medium text-base400">
              {releaseInfo?.artist.name}
            </div>
            <div className="text-[70px] font-medium leading-[100%]">{releaseInfo?.title}</div>
          </div>
          <CircleLogo className="h-[74px] w-[74px] flex-shrink-0" />
        </div>
      </div>
    </ScreenshotContainer>
  );

  return (
    <div className={clsx('flex flex-col gap-4', className)}>
      {showSkeleton ? skeletonBody : body}
      {fullWidthButton ? (
        <Button
          variant="primary"
          label="Download JPG"
          leadingIcon={<FontAwesomeIcon icon={faArrowDownToBracket} />}
          disabled={showSkeleton}
          onClick={onDownload}
          fullWidth
          className="p-0 text-base-l"
        />
      ) : (
        <Button
          variant="text"
          label="Download JPG"
          leadingIcon={<FontAwesomeIcon icon={faArrowDownToBracket} />}
          disabled={showSkeleton}
          onClick={onDownload}
          size="M"
          className="h-auto p-0 text-base-l"
        />
      )}
    </div>
  );
};
